import React, {
  useCallback,
  useContext,
  createContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { isMobile } from 'react-device-detect';
// Api
import {
  HOMEPAGE_SEARCH,
  HOMEPAGE_SEARCH_STORES_ONLY,
} from 'api/search/queries';
import { SEARCH_PERFORMED } from 'api/analytics/mutations';
// Types
import {
  HomepageSearch,
  HomepageSearchVariables,
} from 'api/search/types/HomepageSearch';
import {
  HomepageSearchStoresOnly,
  HomepageSearchStoresOnlyVariables,
} from 'api/search/types/HomepageSearchStoresOnly';
import { UserRole } from 'api/graphql-global-types';
import { SearchBarGroupProps, SearchBarOptionProps } from '../SearchBar';
import {
  SearchLocation,
  SearchResultType,
} from 'api/graphql-analytics-global-types';
import {
  SearchPerformed,
  SearchPerformedVariables,
} from 'api/analytics/types/SearchPerformed';
import analyticsClient from 'api/analyticsClient';
// Hooks
import { useGetCurrUser } from 'hooks';
import { useStreamNavigation } from 'hooks/useStreamNavigation';
// Helpers
import { getStreamerName } from 'helpers/streams';
import { ShopTab, getActiveShopTab, ShopCategories } from 'helpers/shopTabs';

export type { ShopTab } from 'helpers/shopTabs';

type SearchBarContextType = {
  searchValue: string;
  setSearchValue: (value: string) => void;
  options: SearchBarGroupProps[];
  loading: boolean;
  setSearchFocused: (value: boolean) => void;
  setSelectedOption: (value: SearchBarOptionProps) => void;
  shopTab: ShopTab;
  setShopTab: (tab: ShopTab) => void;
  activeShopTab: ShopTab;
  availableCategories: ShopCategories;
};

const SearchBarContext = createContext<SearchBarContextType>(
  {} as SearchBarContextType
);

const SearchBarContextProvider: React.FC = ({ children }) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [shopTab, setShopTab] = useState<ShopTab>('merch');

  const { data: currentUser } = useGetCurrUser();
  const getStreamNavigationProps = useStreamNavigation(
    currentUser?.me.id || null
  );

  // used to prevent Search Performed API call on initial mount, as a way of optimization
  // API will be called only after first click inside the search bar by checking for "isFirstSearch" and if search bar is focused
  const isFirstSearch = useRef(true);

  const [
    selectedOption,
    setSelectedOption,
  ] = useState<SearchBarOptionProps | null>(null);

  // Main search for streams/products
  const [homepageSearch, { data, loading }] = useLazyQuery<
    HomepageSearch,
    HomepageSearchVariables
  >(HOMEPAGE_SEARCH, {
    ssr: false,
  });

  // Athletes search (stores only - called only when no search term)
  const [
    athletesSearch,
    { data: athletesData, loading: athletesLoading },
  ] = useLazyQuery<HomepageSearchStoresOnly, HomepageSearchStoresOnlyVariables>(
    HOMEPAGE_SEARCH_STORES_ONLY,
    { ssr: false, fetchPolicy: 'cache-first' }
  );

  // Creators search (stores only - called only when no search term)
  const [
    creatorsSearch,
    { data: creatorsData, loading: creatorsLoading },
  ] = useLazyQuery<HomepageSearchStoresOnly, HomepageSearchStoresOnlyVariables>(
    HOMEPAGE_SEARCH_STORES_ONLY,
    { ssr: false, fetchPolicy: 'cache-first' }
  );

  const [analyticsSearch] = useMutation<
    SearchPerformed,
    SearchPerformedVariables
  >(SEARCH_PERFORMED, {
    client: analyticsClient,
  });

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastSearchTerm = useRef<string>('');
  const trimmedSearchedValue = searchValue.trim();
  const isNewSearchTerm: boolean =
    trimmedSearchedValue !== lastSearchTerm.current;

  const handleSendAnalytics = (
    id: string | null = null,
    searchType: SearchResultType | null = null
  ) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (trimmedSearchedValue) {
      analyticsSearch({
        variables: {
          input:
            id && searchType
              ? {
                  term: trimmedSearchedValue,
                  location: SearchLocation.Home,
                  searchResult: { srType: searchType, srId: id },
                }
              : {
                  term: trimmedSearchedValue,
                  location: SearchLocation.Home,
                },
        },
      });
      lastSearchTerm.current = trimmedSearchedValue;
    }
  };

  const initiateAnalyticsWithDebounce = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      handleSendAnalytics();
    }, 8000);
  };

  // initial click inside with searchValue = ""
  useEffect(() => {
    if (searchFocused && isFirstSearch.current && searchValue === '') {
      homepageSearch({ variables: { search: searchValue } });
      athletesSearch({
        variables: { search: searchValue, storeRoles: [UserRole.Athlete] },
      });
      creatorsSearch({
        variables: {
          search: searchValue,
          storeRoles: [UserRole.ContentCreator, UserRole.Organization],
        },
      });
      isFirstSearch.current = false;
    }
  }, [
    searchFocused,
    searchValue,
    homepageSearch,
    athletesSearch,
    creatorsSearch,
  ]);

  // debounce API search
  useEffect(() => {
    if (isNewSearchTerm) {
      initiateAnalyticsWithDebounce();
    }
    const timeout = setTimeout(() => {
      if (!searchFocused && isFirstSearch.current) {
        return;
      }
      // Always call homepage search
      homepageSearch({ variables: { search: searchValue } });
      // Only call stores-only queries when NO search term
      if (!searchValue) {
        athletesSearch({
          variables: { search: '', storeRoles: [UserRole.Athlete] },
        });
        creatorsSearch({
          variables: {
            search: '',
            storeRoles: [UserRole.ContentCreator, UserRole.Organization],
          },
        });
      }
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchValue,
    searchFocused,
    homepageSearch,
    athletesSearch,
    creatorsSearch,
    isFirstSearch.current,
  ]);

  // when searchValue changes and input is focused, initiate search analytics api with debounce
  useEffect(() => {
    if (isNewSearchTerm && searchFocused) {
      initiateAnalyticsWithDebounce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // send analytics if search input is blurred and stop debounce timer
  useEffect(() => {
    if (isNewSearchTerm && !searchFocused) {
      handleSendAnalytics();
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFocused]);

  useEffect(() => {
    if (selectedOption) {
      // send analytics when user selects an autocomplete option along with relevant data (search type and id)
      handleSendAnalytics(selectedOption.id, selectedOption.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  // Compute available categories based on data
  const availableCategories: ShopCategories = useMemo(() => {
    if (!data) {
      return {
        merch: false,
        memorabilia: false,
        experiences: false,
      };
    }
    return {
      merch: (data.getMerchProducts?.entities?.length || 0) > 0,
      memorabilia: (data.getMemorabilia?.entities?.length || 0) > 0,
      experiences: (data.getExperiences?.entities?.length || 0) > 0,
    };
  }, [data]);

  // Get the effective active tab (falls back to first available if current has no results)
  const activeShopTab = useMemo(
    () => getActiveShopTab(availableCategories, shopTab),
    [availableCategories, shopTab]
  );

  const formatLabel = (originalLabel: string): string => {
    // designer set the maximum number of characters of search results to 41 for mobile and 75 for desktop.
    // this is done so it never goes in two lines. adding 3 dots after shortened label to indicate it's not fully displayed.
    if (isMobile) {
      return originalLabel.length > 41
        ? originalLabel.substring(0, 41) + '...'
        : originalLabel;
    } else {
      return originalLabel.length > 75
        ? originalLabel.substring(0, 75) + '...'
        : originalLabel;
    }
  };

  const loadOptions = useCallback(() => {
    const groupedOptions: SearchBarGroupProps[] = [];
    const storeNameContentCreatorOptions: SearchBarOptionProps[] = [];
    const storeNameAthleteOptions: SearchBarOptionProps[] = [];
    const streamNameOptions: SearchBarOptionProps[] = [];

    if (!searchValue) {
      athletesData?.stores?.entities?.forEach((item) => {
        const storeName = item.storeDetails?.storeName?.trim() || '';
        const fullName = `${item?.firstName || ''} ${
          item?.lastName || ''
        }`.trim();
        const name = storeName || fullName;
        const url = `/${item.slug}`;
        const id = item.id || '';
        const imageUrl = item.storeDetails?.avatarURL || '';
        const sports = item.sports?.map((sport) => sport.name) || [];
        const username = item.slug ? `@${item.slug}` : '';

        storeNameAthleteOptions.push({
          name,
          searchTerm: storeName,
          label: formatLabel(storeName || name),
          value: storeName + ' ' + searchValue,
          url,
          id,
          type: SearchResultType.Athlete,
          imageUrl,
          sports,
          username,
        });
      });

      creatorsData?.stores?.entities?.forEach((item) => {
        const storeName = item.storeDetails?.storeName?.trim() || '';
        const fullName = `${item?.firstName || ''} ${
          item?.lastName || ''
        }`.trim();
        const name = storeName || fullName;
        const role = item.role;
        const url = `/${item.slug}`;
        const id = item.id || '';
        const imageUrl = item.storeDetails?.avatarURL || '';
        const sports = item.sports?.map((sport) => sport.name) || [];
        const username = item.slug ? `@${item.slug}` : '';

        storeNameContentCreatorOptions.push({
          name,
          searchTerm: storeName,
          label: formatLabel(storeName || name),
          value: storeName + ' ' + searchValue,
          url,
          id,
          type:
            role === UserRole.Organization
              ? SearchResultType.Organization
              : SearchResultType.ContentCreator,
          imageUrl,
          sports,
          username,
        });
      });

      if (storeNameAthleteOptions.length > 0) {
        groupedOptions.push({
          label: 'Top Athletes',
          options: storeNameAthleteOptions.slice(0, 3),
        });
      }

      if (storeNameContentCreatorOptions.length > 0) {
        groupedOptions.push({
          label: 'Top Creators',
          options: storeNameContentCreatorOptions.slice(0, 3),
        });
      }
    } else {
      // With search term: use data.stores filtered by role
      data?.stores?.entities?.forEach((item) => {
        const storeName = item.storeDetails?.storeName?.trim() || '';
        const fullName = `${item?.firstName || ''} ${
          item?.lastName || ''
        }`.trim();
        const name = storeName || fullName;
        const role = item.role;
        const url = `/${item.slug}`;
        const id = item.id || '';
        const imageUrl = item.storeDetails?.avatarURL || '';
        const sports = item.sports?.map((sport) => sport.name) || [];
        const username = item.slug ? `@${item.slug}` : '';

        if (role === UserRole.Athlete) {
          storeNameAthleteOptions.push({
            name,
            searchTerm: storeName,
            label: formatLabel(storeName || name),
            value: storeName + ' ' + searchValue,
            url,
            id,
            type: SearchResultType.Athlete,
            imageUrl,
            sports,
            username,
          });
        } else if (
          role === UserRole.ContentCreator ||
          role === UserRole.Organization
        ) {
          storeNameContentCreatorOptions.push({
            name,
            searchTerm: storeName,
            label: formatLabel(storeName || name),
            value: storeName + ' ' + searchValue,
            url,
            id,
            type:
              role === UserRole.Organization
                ? SearchResultType.Organization
                : SearchResultType.ContentCreator,
            imageUrl,
            sports,
            username,
          });
        }
      });

      if (storeNameAthleteOptions.length > 0) {
        groupedOptions.push({
          label: 'Athletes',
          options: storeNameAthleteOptions.slice(0, 3),
        });
      }

      if (storeNameContentCreatorOptions.length > 0) {
        groupedOptions.push({
          label: 'Creators',
          options: storeNameContentCreatorOptions.slice(0, 3),
        });
      }
    }

    data?.streamsV2.entities?.forEach((item) => {
      const storeName = getStreamerName(item.store);
      const streamName = item.name || `${storeName}'s stream`;
      const id = item.id || '';

      streamNameOptions.push({
        name: streamName,
        searchTerm: streamName,
        label: formatLabel(streamName),
        value: streamName + ' ' + searchValue,
        url: getStreamNavigationProps(item).linkUrl,
        id,
        type: SearchResultType.Stream,
        imageUrl: item.imageURL || '',
        price: item.price,
        isFree: item.isFree,
        storeName: storeName,
        scheduleDate: item.scheduleDate || '',
      });
    });

    if (streamNameOptions.length > 0) {
      groupedOptions.push({
        label: searchValue ? 'Watch' : 'Latest Streams',
        options: streamNameOptions.slice(0, 3),
      });
    }

    // Add media posts
    const mediaPostOptions: SearchBarOptionProps[] = [];
    data?.getMediaPosts?.entities?.forEach((item) => {
      const isVideo = item.__typename === 'VideoPost';
      const thumbnailUrl =
        (item as { mainImageUrl?: string }).mainImageUrl ||
        (item as { videoThumbnailUrl?: string }).videoThumbnailUrl ||
        '';
      const mediaPath = isVideo
        ? 'social-media-post/video'
        : 'social-media-post';
      mediaPostOptions.push({
        name: item.title,
        searchTerm: item.title,
        label: formatLabel(item.title),
        value: item.title + ' ' + searchValue,
        url: `/${item.store?.slug}/${mediaPath}/${item.slug}`,
        id: item.id,
        type: SearchResultType.MediaPost,
        imageUrl: thumbnailUrl,
        storeName:
          (item.store as { storeDetails?: { storeName?: string } })
            ?.storeDetails?.storeName || '',
        isVideo,
      });
    });

    if (mediaPostOptions.length > 0) {
      groupedOptions.push({
        label: 'Media Posts',
        options: mediaPostOptions.slice(0, 3),
      });
    }

    // Separate arrays for each product type
    const merchOptions: SearchBarOptionProps[] = [];
    const memorabiliaOptions: SearchBarOptionProps[] = [];
    const experienceOptions: SearchBarOptionProps[] = [];

    // Add merch products
    data?.getMerchProducts.entities?.forEach((item) => {
      merchOptions.push({
        name: item.title,
        searchTerm: item.title,
        label: formatLabel(item.title),
        value: item.title + ' ' + searchValue,
        url: `/${item.store?.slug}/merch/${item.slug}`,
        id: `${item.id}`,
        type: SearchResultType.Merch,
        imageUrl: item.mainImageUrl || '',
        price: item.minPrice,
        storeName: item.store?.storeDetails?.storeName || '',
      });
    });

    // Add memorabilia
    data?.getMemorabilia.entities?.forEach((item) => {
      const mainImage = item.images?.find((img) => img.isMainImage);

      memorabiliaOptions.push({
        name: item.title,
        searchTerm: item.title,
        label: formatLabel(item.title),
        value: item.title + ' ' + searchValue,
        url: `/${item.store.slug}/memorabilia/${item.slug}`,
        id: item.id,
        type: SearchResultType.Memorabilia,
        imageUrl: mainImage?.url || '',
        price: item.price,
        storeName: item.store?.storeDetails?.storeName || '',
      });
    });

    // Add experiences
    data?.getExperiences.entities?.forEach((item) => {
      experienceOptions.push({
        name: item.title,
        searchTerm: item.title,
        label: formatLabel(item.title),
        value: item.title + ' ' + searchValue,
        url: `/${item.store?.slug}/experience/${item.slug}`,
        id: item.id,
        type: SearchResultType.Experience,
        imageUrl: item.mainImageUrl || '',
        price: item.price,
        storeName: item.store?.storeDetails?.storeName || '',
      });
    });

    // Get options based on selected shop tab
    const getShopOptions = (tab: ShopTab) => {
      switch (tab) {
        case 'merch':
          return merchOptions;
        case 'memorabilia':
          return memorabiliaOptions;
        case 'experiences':
          return experienceOptions;
      }
    };

    // Check if any shop options exist
    const hasShopOptions =
      merchOptions.length > 0 ||
      memorabiliaOptions.length > 0 ||
      experienceOptions.length > 0;

    // Use activeShopTab which already handles fallback to first available tab
    const shopOptions = getShopOptions(activeShopTab);

    if (hasShopOptions && shopOptions.length > 0) {
      groupedOptions.push({
        label: searchValue ? 'Shop' : 'Latest Drops',
        options: shopOptions.slice(0, 3),
        // Pass available categories for tabs
        availableCategories: {
          merch: merchOptions.length > 0,
          memorabilia: memorabiliaOptions.length > 0,
          experiences: experienceOptions.length > 0,
        },
        allOptions: {
          merch: merchOptions,
          memorabilia: memorabiliaOptions,
          experiences: experienceOptions,
        },
      });
    }

    return groupedOptions;
  }, [
    data,
    athletesData,
    creatorsData,
    searchValue,
    activeShopTab,
    getStreamNavigationProps,
  ]);

  const options = loadOptions();

  const isLoading = loading || athletesLoading || creatorsLoading;

  const value = useMemo(
    () => ({
      searchValue,
      setSearchValue,
      options,
      loading: isLoading,
      setSearchFocused,
      setSelectedOption,
      shopTab,
      setShopTab,
      activeShopTab,
      availableCategories,
    }),
    [
      searchValue,
      setSearchValue,
      options,
      isLoading,
      setSearchFocused,
      setSelectedOption,
      shopTab,
      setShopTab,
      activeShopTab,
      availableCategories,
    ]
  );

  return (
    <SearchBarContext.Provider value={value}>
      {children}
    </SearchBarContext.Provider>
  );
};

const useSearchBarContext = (): SearchBarContextType => {
  const context = useContext(SearchBarContext);

  if (!context) {
    throw new Error(
      'useSearchBarContext must be used within a SearchBarContextProvider'
    );
  }

  return context;
};

export { SearchBarContextProvider, useSearchBarContext };
